<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  MODES,
  MODE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  carrierInput,
  createCarrier,
  deleteCarrier,
  listCarriers,
  parseModes,
  updateCarrier,
  type Carrier,
  type CarrierStatus,
  type TransportMode,
} from '../api/carriers'

const rows = ref<Carrier[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', mode: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Carrier | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive({
  code: '',
  name: '',
  modes: [] as TransportMode[],
  contact: '',
  phone: '',
  status: 'active' as CarrierStatus,
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入承运商编码'))

const modesError = computed(() => (form.modes.length ? '' : '请至少选择一种运输方式'))

const formErrorText = computed(() => codeError.value || modesError.value)

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listCarriers({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      mode: query.mode || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    modes: ['truck'] as TransportMode[],
    contact: '',
    phone: '',
    status: 'active' as CarrierStatus,
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Carrier): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    modes: parseModes(row.modes),
    contact: row.contact,
    phone: row.phone,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = carrierInput({ ...form })
  try {
    if (editing.value) await updateCarrier(editing.value.id, input)
    else await createCarrier(input)
    formOpen.value = false
    await load(editing.value ? '承运商已更新' : '承运商已创建')
  } catch (e) {
    // 编码重复是 409，这里原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Carrier): Promise<void> {
  notice.value = ''
  error.value = null
  try {
    await deleteCarrier(row.id)
    await load(`已删除承运商「${row.code}」`)
  } catch (e) {
    // 已被计划区段引用时后端 409
    error.value = e
  }
}
</script>

<template>
  <div class="yc-carrier">
    <header class="yc-carrier__bar">
      <el-input
        v-model="query.keyword"
        class="yc-carrier__keyword"
        placeholder="编码 / 名称 / 联系人"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-carrier__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.mode" class="yc-carrier__ref" placeholder="全部运输方式" clearable @change="search">
        <el-option v-for="m in MODES" :key="m" :label="MODE_LABELS[m]" :value="m" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'mtm:carrier:create'" type="primary" @click="openCreate">新建承运商</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-carrier__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-carrier__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-carrier__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="编码" min-width="120" />
        <el-table-column prop="name" label="名称" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.name || '—' }}</template>
        </el-table-column>
        <el-table-column label="可承运方式" min-width="190">
          <template #default="{ row }">
            <el-tag
              v-for="m in parseModes(row.modes)"
              :key="m"
              class="yc-carrier__mode"
              size="small"
              type="info"
            >
              {{ MODE_LABELS[m] }}
            </el-tag>
            <span v-if="!parseModes(row.modes).length">—</span>
          </template>
        </el-table-column>
        <el-table-column label="联系人" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ [row.contact, row.phone].filter(Boolean).join(' ') || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as CarrierStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as CarrierStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'mtm:carrier:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm
              title="确认删除该承运商？已被计划区段引用的承运商不能删除。"
              width="280"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'mtm:carrier:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有承运商"
        description="联运计划的每个区段都要挂一个承运商，先建档案再编排计划。"
      >
        <el-button v-perm="'mtm:carrier:create'" type="primary" @click="openCreate">新建承运商</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-carrier__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑承运商 · ${editing.code}` : '新建承运商'"
      width="520px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-carrier__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="编码" :error="codeError">
          <el-input v-model="form.code" maxlength="32" placeholder="如 CY001" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 顺达多式联运，可留空" />
        </el-form-item>

        <el-form-item label="可承运方式" :error="modesError">
          <el-select v-model="form.modes" class="yc-carrier__full" multiple placeholder="至少选择一种">
            <el-option v-for="m in MODES" :key="m" :label="MODE_LABELS[m]" :value="m" />
          </el-select>
        </el-form-item>

        <el-form-item label="联系人">
          <el-input v-model="form.contact" maxlength="64" placeholder="如 李师傅，可留空" />
        </el-form-item>

        <el-form-item label="联系电话">
          <el-input v-model="form.phone" maxlength="32" placeholder="如 13800000000，可留空" />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-carrier__hint yc-carrier__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-carrier {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 200px;
  }

  &__ref {
    width: 150px;
  }

  &__full {
    width: 100%;
  }

  &__mode {
    margin-right: var(--yc-space-1);
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
